import { useSize } from 'ahooks';
import { keyBy } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Board } from 'components/organisms/kanban-board/board';
import {
  DropEndResult,
  OnCreateFn,
} from 'components/organisms/kanban-board/types';
import {
  parseDraggableId,
  parseDroppableId,
  toLookup,
} from 'components/organisms/kanban-board/utils';
import {
  FieldType,
  SingleSelectField,
  TextField,
} from 'components/organisms/tasks/fields';
import { FilterFunction } from 'components/organisms/tasks/filter-functions';
import { useCreateTask } from 'components/organisms/tasks/use-create-task';
import { toRows } from 'components/organisms/tasks/utils';
import { PermissionRuleName } from 'constant';
import { RootState } from 'data';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useGetAllMembersNotInProjects } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { Empty } from 'stores/core';
import {
  FIELD_ID_PROJECT_PHASE_FIELD,
  FieldsStore,
  TaskField,
} from 'stores/fields';
import { queueMacrotask } from 'utils/macrotask';
import { BoardLoading } from './loading';
import { PartialValue, useUpdateTasks } from './use-custom-update-tasks';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: scroll;
`;

const excludedTaskFields = [
  'taskId',
  'taskDescription',
  'type',
  'isCsatEnabled',
  'project',
];

interface TaskKanbanBordProps {
  projectId: ProjectId;
  initialized: boolean;
  filterFunction: FilterFunction | null;
  groupBy: {
    rowGroup: string | null;
    columnGroup: string;
  };
  projectField?: SingleSelectField & TaskField;
  taskNameField?: TextField & TaskField;
  isLoading?: boolean;
}

export const TaskBoard: FC<TaskKanbanBordProps> = ({
  filterFunction,
  projectId: [, projectId],
  initialized,
  groupBy: { rowGroup: selectedRowField, columnGroup: selectedColumnField },
  projectField,
  taskNameField,
  isLoading,
}) => {
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const fields = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedTaskFields.includes(fieldName),
      ),
    [fieldsStore],
  );

  const fields_loading = useMemo(
    () => !FieldsStore.loaded(fieldsStore),
    [fieldsStore],
  );

  const fields_lookup_by_name = useMemo(
    () => keyBy(fields, 'fieldName') as Record<string, TaskField>,
    [fields],
  );

  const { byProjectId, byId } = useSelector(
    ({ tasks_v2 }: RootState) => tasks_v2,
  );

  const taskIds = useMemo(
    () => byProjectId[projectId] ?? Empty(),
    [byProjectId, projectId],
  );

  const status = taskIds[0];
  const tasks_loading = status !== 'loaded' && status !== 'refreshing';
  const loading = fields_loading || tasks_loading;

  const filtered = filterFunction != null;
  const [rows, setRows] = useState<Record<string, unknown>[]>();

  useEffect(() => {
    if (loading) {
      return;
    }
    setRows(toRows(taskIds[1] ?? [], byId));
  }, [loading, taskIds, byId, setRows, filtered]);

  const tasksStore = useSelector(({ tasks_v2 }: RootState) => tasks_v2);
  const project = useSelector(
    ({ projects_v2 }: RootState) => projects_v2.byId?.[projectId]?.[1],
  );
  const translate = useTranslate();

  const projectIds = useMemo(() => [projectId], [projectId]);

  const usersInProjects = useGetAllMembersNotInProjects({
    isPrivate: false,
    projectIds,
  });

  const lookup = useMemo(
    () =>
      toLookup(
        fields.filter(
          (field) =>
            [
              FieldType.Assignee,
              FieldType.Date,
              FieldType.SingleSelect,
            ].includes(field.type) ||
            field.fieldId === FIELD_ID_PROJECT_PHASE_FIELD,
        ),
        translate,
        usersInProjects,
        project,
      ),
    [fields, project, translate, usersInProjects],
  );

  const swimlanes = useMemo(() => {
    if (selectedRowField != null) {
      const result = lookup[selectedRowField];

      return result ? Object.values(result) : [];
    }

    return [];
  }, [selectedRowField, lookup]);

  const columns = useMemo(() => {
    const result = lookup[selectedColumnField];

    return result ? Object.values(result) : [];
  }, [selectedColumnField, lookup]);

  const updateTask = useUpdateTasks();

  const handleChange = (result: DropEndResult) => {
    const { source, destination } = result;
    const columnField = fields_lookup_by_name[selectedColumnField];

    if (destination && source && columnField) {
      const destinationDroppableId = destination.droppableId;
      const { draggableId } = source;
      const [sourceRowId, , taskId] = parseDraggableId(draggableId);
      const taskResult = tasksStore.byId[taskId];

      if (taskResult && destinationDroppableId && sourceRowId) {
        const [, task] = taskResult;

        if (task) {
          const columnLookup = lookup[selectedColumnField];
          const [destinationRowId, destinationColumnId] = parseDroppableId(
            destinationDroppableId,
          );
          const to = columnLookup[destinationColumnId]?.value;
          const fromCol = task[selectedColumnField];
          const updates: PartialValue[] = [
            { field: columnField, from: fromCol, to },
          ];

          if (
            destinationRowId &&
            sourceRowId !== destinationRowId &&
            selectedRowField
          ) {
            const rowField = fields_lookup_by_name[selectedRowField];
            const rowLookup = lookup[selectedRowField];
            const rowTo = rowLookup[destinationRowId]?.value;
            const fromRow = task[selectedRowField];

            updates.push({ field: rowField, from: fromRow, to: rowTo });
          }
          updateTask([{ record: task, updates }]);
        }
      }
    }
  };

  // end of board filter
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const createTask = useCreateTask();

  const onCreate: OnCreateFn = useCallback(
    (task, col, swimlane) => {
      if (projectField && taskNameField) {
        const columnField = fields_lookup_by_name[col.dataKey];
        const updates = [
          { field: projectField, to: projectId },
          { field: taskNameField, to: task.taskName as string },
          { field: columnField, to: col.value },
        ];

        if (swimlane) {
          const rowField = fields_lookup_by_name[swimlane.dataKey];

          if (rowField) {
            updates.push({ field: rowField, to: swimlane.value });
          }
        }

        queueMacrotask(() => {
          createTask(updates);
        });
      }
    },
    [createTask, projectId, fields_lookup_by_name, projectField, taskNameField],
  );
  const { isAllowedForCurrentUser } = usePermissionForRule();

  return (
    <Container ref={ref}>
      {!initialized || loading || isLoading ? (
        <BoardLoading />
      ) : (
        <Board
          size={size}
          disableCrossDrop={
            selectedRowField === 'phaseName' &&
            !isAllowedForCurrentUser(
              PermissionRuleName.TaskMoveBetweenPhaseAction,
            )
          }
          onCreate={onCreate}
          swimlanes={swimlanes}
          columns={columns}
          dataSource={
            filterFunction
              ? (rows ?? []).filter((record) => filterFunction(record))
              : rows ?? []
          }
          onChange={handleChange}
          currentProjectId={ProjectId(projectId)}
        />
      )}
    </Container>
  );
};
